import { useContext, useEffect, useState } from 'react';
import { PageTitleContext } from 'context/PageTitleContext';
import BroadcastFilter from 'components/Reports/BroadcastFilter';
import { CogIcon, SearchIcon, DocumentDownloadIcon } from '@heroicons/react/outline';
import { BroadcastTable } from 'components/Broadcast/table';
import { ApiConsumer, Backend, retrieveRecording } from 'api/ApiConsumer';
import { fetchCoverArtUrl } from 'hooks/useCoverArt';
import { API_ROUTES } from 'routes/api';
import { CrumbsContext } from 'context/CrumbsContext';
import { stationData } from 'api/station_data';
import { MusicPlayerContext } from 'context/MusicPlayer';
import { sub } from 'date-fns';
import Loader from 'components/Loader';
import icon from 'assets/images/music-icon.svg';
import { NotificationContext } from 'context/NotificationContext';


export default function Reports() {
  const [details, setDetails] = useState({
    start_date: sub(new Date(), { months: 1 }),
    end_date: new Date(),
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [recording, setRecording] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  let { setTitle } = useContext(PageTitleContext);
  const { setPlaying } = useContext(MusicPlayerContext);
  let { setCrumbs } = useContext(CrumbsContext);
  const [downloadingReport, setDownloadingReport] = useState(false);
  let { setNotification } = useContext(NotificationContext);

  useEffect(() => {
    setTitle('Broadcast Reports');
    setCrumbs([
      { name: 'Charts & Reports', link: '/reports' },
      {
        name: 'Broadcast Reports',
        link: '/reports/broadcast',
      },
    ]);
  }, []);

  const playRecording = (item) => {    
    if (recording.id === item.id) {
      setPlaying({
        music: recording,
      });
    } else if (item.file || item.recording) {
      setPlaying({
        music: item.file || item.recording,
      });
    } else {
      retrieveRecording(item, async (track) => {
        const img = await fetchCoverArtUrl(item.artist, item.title);
        Object.assign(track.music, { image: img || icon });
        if(item?.title){
          Object.assign(track.music, { title: `${item.title}`})
        }

        setRecording(track.music);
        setPlaying(track);
      },(err) => {
        setNotification({
          show: true,
          title: 'Playback Failed',
          message: `The recording is not available yet, try again later`,
          type: 'error',
        })
      },);
    }
  };

  function searchReport() {
    setLoading(true);
    Backend.get(API_ROUTES.BROADCAST.REPORTS.QUERY, {
      params: { ...details, page: page },
      paramSerializer: function (params) {
        return JSON.stringify(params);
      },
    })
      .then((res) => {
        const finalRes = res.data.data?.map((item) => {
          const channelInfo = stationData[item.broadcast_stream_id];
          item.broadcast_stream = {
            ...item.broadcast_stream,
            ...channelInfo,
          };
          return item;
        });

        setResults(finalRes);
        setTotal(res.data.count);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }

  function downloadReport() {
    if (total > 1000000){
      return
    }
    setDownloadingReport(true);
    ApiConsumer.get(API_ROUTES.BROADCAST.REPORTS.BROADCAST_DOWNLOAD, {
      params: { ...details, page: page },
      paramSerializer: function (params) {
        return JSON.stringify(params);
      },
      responseType: 'blob',
    })
      .then(({ data }) => downloadExcelDocument('Broadcast Report.xlsx', data))
      .catch((err) => console.error(err))
      .finally(() => setDownloadingReport(false));
  }

  const downloadExcelDocument = (name, data) => {
    const downloadUrl = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  
  useEffect(() => {
    searchReport();
  }, [page]);

  return (
    <>
      <div className="h-full px-5 py-5 mt-0 mb-20">
        <BroadcastFilter details={details} setDetails={setDetails} />
        <h3 className="font-semibold profile-overview-title mt-5 flex mx-full">
          <span className="min-w-0 flex-1 items-center">Results ({total})</span>
          <SearchIcon
            className="h-8 w-8 pt-1.5 cursor-pointer hover:text-green-500"
            aria-hidden="true"
            onClick={searchReport}
          />
          { downloadingReport ? (
            <CogIcon className="animate-spin h-8 w-8 pt-1.5 cursor-pointer text-sabc-blue" />
          ) : 
          
          <DocumentDownloadIcon
            // className="h-8 w-8 pt-1.5 cursor-pointer hover:text-green-500"
            className={(total > 1000000) ? "h-8 w-8 pt-1.5 cursor-not-allowed hover:text-grey-500" : "h-8 w-8 pt-1.5 cursor-pointer hover:text-green-500"}
            aria-hidden="true"
            onClick={downloadReport}
          />
        }
        </h3>
        { loading ? (
            <Loader upload_message="loading" full_height={true} />
          ) :
          <BroadcastTable
            onPlay={playRecording}
            data={results}
            loading={loading}
            currentPage={page}
            setPage={setPage}
            total={total}
            loadRecording={true}
          />
        }
      </div>
    </>
  );
}
